import extractConfig from './utils/extractConfig';
import { PerformanceDatapoint } from './datapoints/PerformanceDatapoint';
import { PerformanceCoreDatapoint } from './datapoints/PerformanceCoreDatapoint';
import type { ActionMessage, DispatchFunction } from 'satcheljs';
import type RequestOptions from 'owa-service/lib/RequestOptions';
import type { CoreDatapointConfig, DatapointConfig } from 'owa-analytics-types';
import type { MailboxInfo } from 'owa-client-types';
import { lazyTrackNetworkResponse } from './lazyFunctions';
import type {
    DatapointConfigWithFunctions,
    CoreDatapointConfigWithFunctions,
} from './utils/extractConfig';
import { getActionMetadata } from './utils/getActionMetadata';
import type { AnalyticsCoreEventNames } from 'owa-analytics-events';
import { getAnalyticsAddon } from 'owa-analytics-addons';
import { addToTimingMap } from 'owa-performance/lib/utils/timingMap';

const MAX_ACTIONS_TO_KEEP = 7;

let actionDatapointStack: PerformanceDatapoint[] = [];
export function getActionDatapointStack(): PerformanceDatapoint[] {
    return actionDatapointStack;
}

/**
 * Datapoints transferred to lazy clone actions, innermost last. Separate from
 * `actionDatapointStack` because `trackServiceResponses` reads that stack's
 * index 0 for service-response attribution, which must not change.
 */
let transferredDatapointStack: PerformanceDatapoint[] = [];
export function getTransferredDatapointStack(): PerformanceDatapoint[] {
    return transferredDatapointStack;
}

export function clearActionDatapointStack(): void {
    actionDatapointStack = [];
    transferredDatapointStack = [];
    declinedInvocationStack.length = 0;
}

export function returnTopExecutingActionDatapoint(
    filter: string | ((dp: PerformanceDatapoint) => boolean)
): PerformanceDatapoint | null | undefined {
    const matches = (dp: PerformanceDatapoint) =>
        typeof filter == 'function' ? filter(dp) : dp.getEventName() == filter;

    // A lazy clone handler runs under a transferred datapoint, which is not on
    // `actionDatapointStack`. Prefer the innermost transferred datapoint that
    // matches, so a clone enriches the action it is actually executing under
    // rather than an unrelated datapoint that happens to share the event name.
    for (let i = transferredDatapointStack.length - 1; i >= 0; i--) {
        const candidate = transferredDatapointStack[i];
        if (matches(candidate)) {
            return candidate;
        }
    }

    return actionDatapointStack.filter(matches)[0];
}

/**
 * One entry per executing invocation that has a datapoint, innermost last.
 * Pushed by `addDataPointInternal`, always popped in its `finally`.
 */
const declinedInvocationStack: boolean[] = [];

/**
 * Called by a lazy orchestrator that received a transferred datapoint for an
 * action it does not own, so the middleware does not auto-end it.
 *
 * Ownership must be disclaimed explicitly: handlers returning `undefined`/`void`
 * remain owners, so existing synchronous owners keep auto-ending.
 *
 * A no-op unless a datapoint invocation is synchronously executing — the action
 * may carry no datapoint, or the frame may already have unwound across an
 * `await`. Both are expected, and recording the intent would leak it into an
 * unrelated later invocation.
 */
export function declineActionDatapoint(): void {
    if (declinedInvocationStack.length > 0) {
        declinedInvocationStack[declinedInvocationStack.length - 1] = true;
    }
}

function trackServiceResponses(
    responsePromise: Promise<Response>,
    actionName: string,
    _url: string,
    _attemptCount: number,
    optionsPromise: Promise<RequestOptions>
) {
    var datapoint = actionDatapointStack[0];
    if (datapoint) {
        lazyTrackNetworkResponse.importAndExecute(
            datapoint,
            responsePromise,
            actionName,
            optionsPromise
        );
    }
}

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function executeWhenReady called at module scope is not allowed. This may cause unwanted side effects.
 *	> Function getAnalyticsAddon called at module scope is not allowed. This may cause unwanted side effects. */
getAnalyticsAddon('RegisterCustomOwsCallbacks')?.executeWhenReady(trackServiceResponses);

export function addDatapointMiddleware(next: DispatchFunction, actionMessage: ActionMessage): any {
    const actionMetadata = getActionMetadata();
    actionMetadata.count++;
    const actionType = actionMessage.type;

    while (actionMetadata.queue.length >= MAX_ACTIONS_TO_KEEP) {
        actionMetadata.queue = actionMetadata.queue.slice(1);
    }
    if (actionType) {
        addToTimingMap('satchelAction', actionType);
        actionMetadata.queue.push(actionType);
        actionMetadata.actionMap.set(
            actionType,
            (actionMetadata.actionMap.get(actionType) ?? 0) + 1
        );
    }

    // if there is a datapoint config or action datapoint
    if (actionMessage.dp || actionMessage.actionDatapoint) {
        if (actionType) {
            actionMetadata.stack.push(actionType);
        }
        return addDataPointInternal(
            next.bind(null, actionMessage),
            actionMessage.dp,
            actionMessage
        );
    }

    // No datapoint on this action, but it may run synchronously inside an
    // enclosing invocation's frame. Open a throwaway frame so a decline call
    // made during this dispatch lands here instead of leaking into the
    // enclosing invocation's frame.
    declinedInvocationStack.push(false);
    try {
        return next(actionMessage);
    } finally {
        declinedInvocationStack.pop();
    }
}

export function wrapFunctionForDatapoint<
    DatapointArgs extends any[],
    WrappedFunctionArgs extends [...DatapointArgs, ...any[]],
    WrappedFunctionReturnType
>(
    config: DatapointConfigWithFunctions<DatapointArgs>,
    funcToWrap: (...args: WrappedFunctionArgs) => WrappedFunctionReturnType
): (...args: WrappedFunctionArgs) => WrappedFunctionReturnType {
    return wrapFunctionForDatapointInternal<
        DatapointArgs,
        WrappedFunctionArgs,
        WrappedFunctionReturnType
    >(config, funcToWrap, false /* isCore */);
}

export function wrapFunctionForCoreDatapoint<
    DatapointArgs extends any[],
    WrappedFunctionArgs extends [...DatapointArgs, ...any[]],
    WrappedFunctionReturnType
>(
    config: CoreDatapointConfigWithFunctions<DatapointArgs>,
    funcToWrap: (...args: WrappedFunctionArgs) => WrappedFunctionReturnType
): (...args: WrappedFunctionArgs) => WrappedFunctionReturnType {
    return wrapFunctionForDatapointInternal<
        DatapointArgs,
        WrappedFunctionArgs,
        WrappedFunctionReturnType
    >(config, funcToWrap, true /* isCore */);
}

function wrapFunctionForDatapointInternal<
    DatapointArgs extends any[],
    WrappedFunctionArgs extends [...DatapointArgs, ...any[]],
    WrappedFunctionReturnType
>(
    config: DatapointConfigWithFunctions<DatapointArgs>,
    funcToWrap: (...args: WrappedFunctionArgs) => WrappedFunctionReturnType,
    isCore: boolean
): (...args: WrappedFunctionArgs) => WrappedFunctionReturnType {
    return config
        ? (...args: WrappedFunctionArgs) => {
              return addDataPointInternal(
                  () => funcToWrap.apply(null, args),
                  extractConfig(config, args as unknown as DatapointArgs),
                  undefined /* actionMessage */,
                  isCore
              );
          }
        : funcToWrap;
}

function addDataPointInternal(
    executeNext: () => any,
    config: (DatapointConfig | CoreDatapointConfig) & {
        skipAutoEndDatapoint?: string;
        mailbox?: MailboxInfo;
    },
    actionMessage?: ActionMessage & {
        lazyOrchestrator?: boolean;
    },
    isCore?: boolean
) {
    let datapoint: PerformanceDatapoint | PerformanceCoreDatapoint | null = null;
    let stackDatapoint: PerformanceDatapoint | PerformanceCoreDatapoint | null = null;
    let transferredDatapoint: PerformanceDatapoint | PerformanceCoreDatapoint | null = null;

    if (actionMessage?.actionDatapoint) {
        datapoint = actionMessage.actionDatapoint;
    } else if (config.name) {
        const options = config.options;
        // Mailbox is now required as argument #2 in all analytics APIs.
        const mailbox = config.mailbox;

        datapoint =
            isCore && !(config as CoreDatapointConfig).disableIsCore
                ? new PerformanceCoreDatapoint(
                      config.name as AnalyticsCoreEventNames,
                      mailbox,
                      options
                  )
                : new PerformanceDatapoint(config.name, mailbox, options);
        // only push the datapoint on the stack if it has just been created
        actionDatapointStack.push(datapoint);
        stackDatapoint = datapoint;

        if (datapoint) {
            try {
                if (config.customData) {
                    datapoint.addCustomData(config.customData);
                }
                if (config.cosmosOnlyData) {
                    datapoint.addCosmosOnlyData(config.cosmosOnlyData);
                }
                if (config.actionSource) {
                    datapoint.addActionSource(config.actionSource);
                }
                if (config.unifiedTelemetry) {
                    datapoint.addUnifiedTelemetryData(config.unifiedTelemetry);
                }
            } catch {
                // ignore errors when trying to add custom data
            }
        }
    }

    // Track a transferred datapoint separately so a clone handler can find the one
    // it is executing under, without disturbing service-response attribution.
    if (datapoint && actionMessage?.actionDatapoint) {
        transferredDatapointStack.push(datapoint);
        transferredDatapoint = datapoint;
    }

    // Open an ownership frame; the `finally` below always closes it.
    declinedInvocationStack.push(false);

    var returnValue: any = null;
    let declinedByNonOwner = false;
    try {
        returnValue = executeNext();
    } catch (error) {
        // A handler that declined does not own this datapoint, even when it throws.
        if (datapoint && !declinedInvocationStack[declinedInvocationStack.length - 1]) {
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'TraceErrorObject | undefined'.
            // @ts-expect-error
            datapoint.endAction(undefined, error);
        }

        throw error;
    } finally {
        declinedByNonOwner = declinedInvocationStack.pop() ?? false;

        getActionMetadata().stack.pop();

        // Remove by identity: an out-of-order unwind must not drop another frame's entry.
        if (stackDatapoint) {
            const stackIndex = actionDatapointStack.lastIndexOf(stackDatapoint);
            if (stackIndex >= 0) {
                actionDatapointStack.splice(stackIndex, 1);
            }
        }

        if (transferredDatapoint) {
            const transferredIndex = transferredDatapointStack.lastIndexOf(transferredDatapoint);
            if (transferredIndex >= 0) {
                transferredDatapointStack.splice(transferredIndex, 1);
            }
        }
    }

    // if it is a lazy orchestrator, we want the clone of the action to log it,
    // not the initial action that imports the bundle
    if (datapoint && actionMessage?.lazyOrchestrator) {
        actionMessage.actionDatapoint = datapoint;
        datapoint = null;
    }

    if (datapoint && !config?.skipAutoEndDatapoint && !declinedByNonOwner) {
        const dp = datapoint;
        // if the return value is not a promise, then let's create a promise and resolve it right away
        // this will capture all of the javascript in the call stack
        const returnPromise = returnValue?.then ? returnValue : Promise.resolve();
        returnPromise
            .then(() => {
                dp.setEndPending();
            })
            .catch((error: Error) => {
                dp.endAction(undefined, error);
            });
    }

    return returnValue;
}
